import { LeftOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Spin } from "antd";
import { useState } from "react";
import { Navigate, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getSales } from "@/modules/sales/services/sales.backend";
import type { AppRole } from "@/shared/config/navigation";
import { parseApiError } from "@/shared/utils/error";
import { ClientOverviewTab } from "../components/ClientOverviewTab";
import { ClientPersonalCard } from "../components/ClientPersonalCard";
import { ClientSalesTab } from "../components/ClientSalesTab";
import { ClientSmsTab } from "../components/ClientSmsTab";
import { deleteClient, getClientById } from "../services/clients.backend";

type ClientDetailLocationState = {
	fromDashboard?: boolean;
	fromDeepNav?: boolean;
	returnTo?: string;
};

type ClientDetailTab = "overview" | "sales" | "sms";

export function ClientDetailPage() {
	const { clientId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { role } = useOutletContext<{ role: AppRole }>();

	const {
		data: client,
		isLoading,
		isError,
	} = useQuery({
		queryKey: ["client", clientId],
		queryFn: () => {
			if (!clientId) {
				throw new Error("Client ID is required");
			}
			return getClientById(clientId);
		},
		enabled: !!clientId,
	});

	const deleteClientMutation = useMutation({
		mutationFn: () => {
			if (!clientId) {
				throw new Error("Client ID is required");
			}
			return deleteClient(clientId);
		},
		onSuccess: async () => {
			message.success("Mijoz muvaffaqiyatli o'chirildi!");
			await queryClient.invalidateQueries({ queryKey: ["clients"] });
			queryClient.removeQueries({ queryKey: ["client", clientId] });
			navigate("/clients");
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const locationState = location.state as ClientDetailLocationState | null;
	const fromDashboard = locationState?.fromDashboard === true;
	const fromDeepNav = locationState?.fromDeepNav === true;
	const returnTo = locationState?.returnTo;

	const [activeTab, setActiveTab] = useState<ClientDetailTab>("overview");

	const { data: clientSales = [], isLoading: isSalesLoading } = useQuery({
		queryKey: ["sales", "client", clientId],
		queryFn: async () => {
			if (!clientId) {
				throw new Error("Client ID is required");
			}
			const result = await getSales({ clientId, limit: 100, page: 1 });
			return result.items;
		},
		enabled: !!clientId && activeTab === "sales",
	});

	const handleActiveTabChange = (tab: string) => {
		if (tab === "overview" || tab === "sales" || tab === "sms") {
			setActiveTab(tab);
		}
	};

	const handleBack = () => {
		if (fromDeepNav || fromDashboard) {
			navigate(-1);
			return;
		}
		if (returnTo) {
			navigate(returnTo);
			return;
		}

		navigate("/clients");
	};

	if (isLoading) {
		return (
			<div style={{ padding: 60, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (isError || !client) {
		return <Navigate replace to="/clients" />;
	}

	return (
		<div className="crm-client-detail">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={handleBack}
						type="text"
					>
						{returnTo === "/reports/debts"
							? "Qarzdorlikka qaytish"
							: fromDashboard || fromDeepNav
								? "Ortga qaytish"
								: "Ro'yxatga qaytish"}
					</Button>
					<h1 style={{ marginTop: "4px" }}>
						{[client.lastName, client.firstName, client.patronymic].filter(Boolean).join(" ")}
					</h1>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Mijoz kartochkasi
					</p>
				</div>
				<div className="crm-head-actions">
					<Button icon={<UnorderedListOutlined />} onClick={() => navigate("/clients")}>
						Ro'yxat
					</Button>
				</div>
			</div>

			{/* Tabs Segment */}
			<div style={{ marginBottom: "16px" }}>
				<div className="crm-seg">
					<button
						className={activeTab === "overview" ? "active" : ""}
						onClick={() => setActiveTab("overview")}
						type="button"
					>
						Umumiy
					</button>
					<button
						className={activeTab === "sales" ? "active" : ""}
						onClick={() => setActiveTab("sales")}
						type="button"
					>
						Sotuvlar
					</button>
					<button
						className={activeTab === "sms" ? "active" : ""}
						onClick={() => setActiveTab("sms")}
						type="button"
					>
						SMS tarixi
					</button>
				</div>
			</div>

			{/* Main Grid Layout */}
			<div className="crm-grid crm-client-detail-grid">
				{/* Left Sidebar Info */}
				<ClientPersonalCard
					client={client}
					isDeleting={deleteClientMutation.isPending}
					onActiveTabChange={handleActiveTabChange}
					onDelete={() => deleteClientMutation.mutate()}
				/>

				{/* Right Tab Content */}
				<div>
					{activeTab === "overview" && <ClientOverviewTab client={client} role={role} />}
					{activeTab === "sales" && (
						<ClientSalesTab client={client} isLoading={isSalesLoading} sales={clientSales} />
					)}
					{activeTab === "sms" && <ClientSmsTab client={client} role={role} />}
				</div>
			</div>
		</div>
	);
}
export default ClientDetailPage;
